import { all, dropTask } from 'ember-concurrency';
import { array, hash } from '@ember/helper';
import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import { courseBadgeState } from 'tio-employee/utils/tas/applicationStateMap';
import Divider from 'tio-reimagined/components/divider';
import {
  getAllValuesForTasField,
  getSingleValueForTasField,
  setSingleValueForTasField,
} from 'tio-common/utils/tuition-assistance/fields';
import { fn } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import Input from 'tio-reimagined/components/input';
import Select from 'tio-reimagined/components/select';
import { inputsForFields } from 'tio-common/utils/data/inputs-for-fields';
import { LinkTo } from '@ember/routing';
import { not, or, and } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, HStack, Section, Header } from 'tio-reimagined/components/layout';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import ActionableList from 'tio-ui/components/actionable-list';
import Alert from 'tio-employee/components/tas/applications/alert';
import TasFaqs from 'tio-common/components/tas/faqs';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import ApplicationProcess from 'tio-employee/components/tuition-assistance/application/application-process';
import ClaimsFinancialsCalculationsReadonlyCard from 'tio-common/components/tuition-assistance/program-details/claims-financials/calculations-readonly-card';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import CourseSubmitted from 'tio-employee/components/tas/applications/course-submitted';
import DescriptionList from 'tio-ui/components/description-list';
import { divide } from 'tio-ui/math';
import NavTabs from 'tio-reimagined/components/nav-tabs';
import PaymentDetailsCard from 'tio-common/components/tas/payment-details-card';
import ProgramCustomReadonlyField from 'tio-common/components/tuition-assistance/program-custom-readonly-field';
import ProgramReadonlyField from 'tio-common/components/tuition-assistance/program-readonly-field';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatFieldValue from 'tio-common/helpers/safe-format-field-value';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import ShowCourseDrawer from 'tio-employee/components/tas/show-course-drawer';
import Table from 'tio-reimagined/components/table';
import TasApplicationsShowController from 'tio-employee/controllers/authenticated/tas/applications/show';
import TASCourseModel from 'tio-data/models/tas-course';
import ErrorMessages from 'tio-reimagined/components/error-messages';
import { PhChatCenteredText, PhTrash } from 'ember-phosphor-icons';
import PartnerMessagingDrawer from 'tio-employee/components/drawers/partner-messaging-drawer';
import type PartnerService from 'tio-employee/services/partner';
import type LpnPage from 'tio-data/models/lpn-page';
import type { TransitionLogSignature } from 'tio-common/types/tuition-assistance';
import type ConditionModel from 'tio-data/models/condition';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplicationAdapter from 'tio-employee/adapters/tas-application';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TASProgramTemplateModel from 'tio-data/models/tas-program-template';
import { abandonProgramRequest } from 'tio-common/requests/tas-program-instance';
import { buildTasEmployeeActor } from 'tio-common/utils/tas-actor';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import CategorizedUpload from 'tio-common/components/tuition-assistance/forms/categorized-upload';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import Checkbox from 'tio-reimagined/components/checkbox';
import type AgreementModel from 'tio-data/models/agreement';
import type FeaturesService from 'tio-common/services/features';
import {
  PhPencil,
  PhCheckCircle,
  PhCircle,
  PhCaretLeft,
  PhClipboardText,
  PhInfo,
} from 'ember-phosphor-icons';
import StripeCardDisplay from 'tio-employee/components/tas/course-cash/stripe-card-display';
import formatDateString from 'tio-common/helpers/format-date-string';
import ReceiptBanner from 'tio-employee/components/tas/course-cash/receipt-banner';
import ReceiptUploadModal from 'tio-employee/components/tas/course-cash/receipt-upload-modal';
import { mapAssistancesToTransactions } from 'tio-employee/utils/course-cash/transaction-adapter';
import type {
  CourseCashReceipt,
  CourseCashTransaction,
} from 'tio-common/types/course-cash-transaction';
import type DigitalAssetsService from 'tio-common/services/digital-assets';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';
import type TasAssetModel from 'tio-data/models/tas-asset';
import type AttachmentModel from 'tio-data/models/attachment';
import { abandonTasApprovableRequest } from 'tio-common/requests/tas-approvable';
import type ApprovableModel from 'tio-data/models/tas-approvable';
import type BetaExperienceService from 'tio-employee/services/beta-experience';

interface RouteSignature {
  Args: {
    model: {
      application: TasApplicationModel;
      dynamicEligibility: boolean;
      programEligibility: [string, string][];
    };
    controller: TasApplicationsShowController;
  };
}

type LockEmployeeStatusBasedOnType =
  | 'COURSE_START_DATE'
  | 'COURSE_END_DATE'
  | 'FULFILLED_DATE'
  | 'ON_COURSES_SUBMISSION'
  | 'ON_SUBMISSION';

const programTypesToIncludeInstanceAssets = new Set(['TAS.ProgramType.2', 'TAS.ProgramType.3']);

export default class AuthenticatedTasApplicationsShowComponent extends Component<RouteSignature> {
  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked submitError = null;
  @tracked hasSubmitted = false;
  @tracked isGradeModalOpen = false;
  @tracked courseToEdit: null | TASCourseModel = null;
  @tracked _pendingGrade: string | null = null;
  @tracked isCourseDrawerOpen = false;
  @tracked courseShowing: null | TASCourseModel = null;
  @tracked deleteApplicationOpen = false;
  @tracked gpaBasedAmount = 0;
  @tracked isPartnerMessagingDrawerOpen = false;
  @tracked courseCashTransactions: CourseCashTransaction[] = [];
  @tracked isReceiptModalOpen = false;
  @tracked selectedReceiptTransaction: CourseCashTransaction | null = null;
  @tracked isLpnSchool = false;
  @tracked highlightAddResult = false;
  @tracked pendingGradePointAverage = '';

  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare features: FeaturesService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare partner: PartnerService;
  @service('beta-experience') declare betaExperience: BetaExperienceService;

  courseApprovalStates = [
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.COURSES_APPROVED',
    'TAS.ApplicationState.COURSES_NOT_APPROVED',
    'TAS.ApplicationState.COURSES_REJECTED',
  ];

  programTemplate: TASProgramTemplateModel;

  constructor(owner: Owner, args: RouteSignature['Args']) {
    super(owner, args);
    this.programTemplate = args.model.application.tasProgramInstance.tasProgramTemplate;

    this.pendingGradePointAverage = this.currentGradePointAverage;
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(
      Number(this.pendingGradePointAverage || 0)
    );

    // Load Course Cash transactions from approvables endpoint + assistance data
    this.loadCourseCashTransactions();

    // Detect if this application's school is an LPN school
    this.detectLpnSchool();
  }

  detectLpnSchool = async () => {
    try {
      const lpnSettings = this.partner.companySettings?.lpn;
      if (!lpnSettings?.isEnabled) return;

      const instanceFields = this.args.model.application.tasProgramInstance.fields;
      const dapipId = getSingleValueForTasField('DAPIP_INSTITUTION_ID', instanceFields);
      if (!dapipId) return;

      const pages = await this.store.query<LpnPage>('lpn-page', {
        filter: { 'dapip-id': String(dapipId) },
        include: 'company-lpn-settings,company-lpn-settings.company-setting',
      });
      if (pages.length === 0) return;

      const setting = await pages[0]?.settingForCompany(this.partner.companySettings.id);
      this.isLpnSchool = setting ? setting.isEnabled : true;
    } catch {
      // Silently fail — LPN checkbox is optional
    }
  };

  toggleLpnDiscountActivated = () => {
    const app = this.args.model.application;
    app.lpnDiscountActivated = !app.lpnDiscountActivated;
  };

  /**
   * Query the /tas-approvables endpoint to get approvable records for this
   * application's assistances, then build the transaction list.
   *
   * The endpoint filters by approval type and context_type and is scoped to
   * the current user by the API's permit_for_user policy.
   */
  loadCourseCashTransactions = async () => {
    const assistances = this.args.model.application.tasAssistances ?? [];
    const approvablesByAssistance = await this.queryApprovablesByAssistance(assistances);
    this.courseCashTransactions = mapAssistancesToTransactions(
      assistances,
      approvablesByAssistance
    );
  };

  /**
   * Query the /tas-approvables endpoint filtered to the assistances in this
   * application and group results by assistance ID.
   */
  queryApprovablesByAssistance = async (
    assistances: { id: string | null }[]
  ): Promise<Map<string, ApprovableModel[]>> => {
    const map = new Map<string, ApprovableModel[]>();

    // Short-circuit when there are no captured Course Cash assistances
    const assistanceIds = new Set(assistances.filter((a) => a.id).map((a) => a.id!));
    if (assistanceIds.size === 0) return map;

    try {
      // Note: cannot include approvable.attachments — JSONAPI::Resources
      // can't traverse through a polymorphic relationship. Attachments are
      // already in the store from the route's tas-assistances.tas-assets.attachments include.
      const approvables = await this.store.query('tas-approvable', {
        filter: {
          approval: 'ApprovalType.COURSE_CASH_USAGE_REVIEW',
          context_type: 'TasAssistance',
          context_id: [...assistanceIds].join(','),
        },
        include: 'approvable,in-context',
      });

      const results = (approvables as { slice(): ApprovableModel[] }).slice();

      for (const approvable of results) {
        // in-context relationship links approvable to its TasAssistance
        const contextId = approvable.inContext?.id;
        if (!contextId || !assistanceIds.has(contextId)) continue;

        const existing = map.get(contextId) || [];
        existing.push(approvable);
        map.set(contextId, existing);
      }
    } catch (error) {
      console.error('[CourseCash] Failed to query approvables endpoint:', error);
      // On failure, the transaction adapter falls back to sideloaded relationship data
    }

    return map;
  };

  get typeClassification() {
    return this.programTemplate.typeClassification;
  }

  get hasPcsCards() {
    return (this.args.model.application.pcsCards?.length ?? 0) > 0;
  }

  get hasUnverifiedCourseCashTransactions() {
    return this.courseCashTransactions.some((t) => t.receiptStatus === 'needed');
  }

  get unverifiedTransactions() {
    return this.courseCashTransactions.filter((t) => t.receiptStatus === 'needed');
  }

  get allAttachments() {
    const { application } = this.args.model;
    const courseAssets = application.activeCourses.flatMap((c) => c.tasAssets ?? []);
    const appAssets = application.tasAssets;
    const programAssets = application.tasProgramInstance.tasAssets ?? [];

    return [
      ...courseAssets,
      ...appAssets,
      // only include programAssets for type 2/3 programs
      ...(programTypesToIncludeInstanceAssets.has(this.typeClassification) ? programAssets : []),
    ];
  }

  get shouldShowAttachments() {
    const typesWithCourseAttachments = [
      'TAS.ProgramType.2',
      'TAS.ProgramType.3',
      'TAS.ProgramType.4',
      'TAS.ProgramType.4.2',
      'TAS.ProgramType.4.3',
    ];
    if (!typesWithCourseAttachments.includes(this.typeClassification)) return false;
    return (
      this.programTemplate.attachmentsForCourseApprovalVisible ||
      this.programTemplate.attachmentsForCourseCompletionVisible ||
      this.args.model.application.state === 'TAS.ApplicationState.ATTEND'
    );
  }

  get useCategorizedUpload() {
    if (!this.betaExperience.isProductEnabled('ta')) return false;
    const entries = this.programTemplate.documentEntriesFor(this.assetClassificationString);
    return entries.length > 0;
  }

  get isAttachmentRequired() {
    if (!this.shouldShowAttachments) return false;
    const typesWithBothFlags = ['TAS.ProgramType.2', 'TAS.ProgramType.4.2'];
    if (typesWithBothFlags.includes(this.typeClassification)) {
      return (
        this.programTemplate.requireAttachmentsForCourseApproval ||
        this.programTemplate.requireAttachmentsForCourseCompletion
      );
    }
    return this.programTemplate.requireAttachmentsForCourseApproval;
  }

  get conditionsForSubmit() {
    return this.programTemplate.applicationApproveEvidenceConditions || [];
  }

  get showSubmitForEvidenceReview() {
    return this.args.model.application.state === 'TAS.ApplicationState.ATTEND';
  }

  get setRequestedTotalAmount() {
    return Math.min(this.gpaBasedAmount, this.args.model.application.requestedTotal);
  }

  submitForEvidenceApproval = dropTask(async () => {
    this.submitError = null;
    this.hasSubmitted = true;
    try {
      await this.saveAgreementsForConditions.perform();
      if (this.isLpnSchool && this.args.model.application.hasDirtyAttributes) {
        await this.args.model.application.save();
      }
      const appAdapter = this.store.adapterFor(
        'tas-application'
      ) as unknown as TasApplicationAdapter;
      await appAdapter.requestCourseEvidence(this.args.model.application);

      const tasType = this.typeClassification === 'TAS.ProgramType.3' ? 'three' : 'four';
      this.router.transitionTo('authenticated.tas.confirmation', {
        queryParams: {
          tasType: tasType,
          applicationId: this.args.model.application.id,
          evidence: 'true',
        },
      });
    } catch (error) {
      console.error('Failed to submit application:', error);
      this.submitError = error;
    }
  });

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });
    const promises = agreements.map((agreement) => {
      return agreement.save();
    });
    const saved = await all(promises);
    return saved;
  });

  get hasAgreedAllConditions() {
    if (!this.conditionsForSubmit.length) {
      return true;
    }
    return this.conditionsForSubmit.length === this.acceptedConditions.length;
  }

  get hasRequiredAttachments() {
    return this.args.model.application.hasRequiredEvidenceAttachments;
  }

  get canSubmitApplication() {
    if (this.args.model.application.ignorePolicies) {
      return true;
    }
    const { isCourseGradeRequired, isMissingCourseGrades, state } = this.args.model.application;

    const sharedConditions =
      this.isActiveParticipant &&
      this.hasAgreedAllConditions &&
      this.hasRequiredAttachments &&
      !this.inEligible &&
      state === 'TAS.ApplicationState.ATTEND' &&
      this.hasGpaIfRequired; // TAS.TODO: rework when updating validation

    return isCourseGradeRequired ? sharedConditions && !isMissingCourseGrades : sharedConditions;
  }

  get hasGpaIfRequired() {
    // checks if the GPA field is required
    if (!this.showGPAField) {
      return true;
    }

    const hasField = Boolean(
      getSingleValueForTasField('GRADE_POINT_AVERAGE', this.args.model.application.fields)
    );

    return hasField;
  }

  get expenseType() {
    const expenseType = getSingleValueForTasField('EXPENSE_TYPE', this.programTemplate.fields);

    return expenseType ? `${expenseType}` : `Education`;
  }

  get courseBadge() {
    return courseBadgeState(this.args.model.application.state);
  }

  get showInstructions() {
    const typeClassificationsToHide = [
      'TAS.ProgramType.3',
      'TAS.ProgramType.4',
      'TAS.ProgramType.4.3',
    ];
    return (
      typeClassificationsToHide.includes(this.typeClassification) &&
      this.args.model.application.state === 'TAS.ApplicationState.ATTEND'
    );
  }

  get grades() {
    const exclusiveGradeOptions = getAllValuesForTasField(
      'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
      this.programTemplate.fields
    ) as string[];
    if (exclusiveGradeOptions.length > 0) {
      return exclusiveGradeOptions;
    }

    return inputsForFields['COURSE_GRADE'].options.map((option) => option.value);
  }

  get selectedGrade() {
    return (
      this._pendingGrade ??
      (getSingleValueForTasField('COURSE_GRADE', this.courseToEdit!.fields) as string)
    );
  }

  get programCustomFields() {
    const customFields = this.args.model.application.tasProgramInstance.customFields;
    return customFields.filter((field: { province: string }) => field.province === 'PROGRAM');
  }

  get isTypeTwoOrThreeApplication() {
    return (
      this.typeClassification === 'TAS.ProgramType.3' ||
      this.typeClassification === 'TAS.ProgramType.2'
    );
  }

  get showAddResult() {
    return this.args.model.application.state === 'TAS.ApplicationState.ATTEND';
  }

  get showCompletedGrade() {
    const showGradeStates = [
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
    ];
    return showGradeStates.includes(this.args.model.application.state);
  }

  get applicationAwaitingApproval() {
    const awaitingStates = [
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ];
    return awaitingStates.includes(this.args.model.application.state);
  }

  get assetClassificationString() {
    return this.courseApprovalStates.includes(this.args.model.application.state)
      ? 'COURSE_APPROVAL_DOCUMENTS'
      : 'COMPLETION_DOCUMENTS';
  }

  get showDependentInfo() {
    const typeClassificationsToCheck = ['TAS.ProgramType.2', 'TAS.ProgramType.3'];
    return (
      this.programTemplate.isDependentProgram &&
      typeClassificationsToCheck.includes(this.typeClassification)
    );
  }

  get lockEmployeeStatusBasedOn() {
    return this.programTemplate.lockEmployeeStatusBasedOn as LockEmployeeStatusBasedOnType;
  }

  get lockStatus() {
    const lockType: Record<LockEmployeeStatusBasedOnType, string> = {
      COURSE_START_DATE: 'start',
      COURSE_END_DATE: 'end',
      FULFILLED_DATE: 'fulfilled',
      ON_COURSES_SUBMISSION: 'submission',
      ON_SUBMISSION: 'submission',
    };
    return this.lockEmployeeStatusBasedOn in lockType
      ? lockType[this.lockEmployeeStatusBasedOn]
      : '';
  }

  get startOrEndDate() {
    if (this.lockStatus === 'start') {
      return getSingleValueForTasField('COURSES_BEGIN_DATE', this.args.model.application.fields);
    }
    if (this.lockStatus === 'end') {
      return getSingleValueForTasField('COURSES_END_DATE', this.args.model.application.fields);
    }
    // For 'submission' lock status, use today's date
    return new Date().toLocaleDateString('en-CA');
  }

  calculateEligibility() {
    const { programEligibility } = this.args.model;
    if (!programEligibility.length) {
      return false;
    }

    const dateValue = this.startOrEndDate;
    // If we're checking start/end dates and they're missing, can't validate eligibility
    if (!dateValue || typeof dateValue !== 'string') {
      return false;
    }

    // Use today's date for 'submission' lock status, otherwise use the provided start or end date.
    const enteredDate = new Date(dateValue);

    return programEligibility.some(([start, end]) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      return enteredDate >= startDate && enteredDate <= endDate;
    });
  }

  get inEligible() {
    const { application, dynamicEligibility } = this.args.model;
    const { ignorePolicies, state } = application;

    if (ignorePolicies || !this.lockStatus) return false;

    const completedStates = [
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];
    if (completedStates.includes(state)) return false;
    if (['fulfilled', 'submission'].includes(this.lockStatus)) return false;

    return !(dynamicEligibility && this.calculateEligibility());
  }

  get requestedChanges() {
    const findNotApprovedDetails = (transitions: TransitionLogSignature[]) => {
      // Filter transitions for "NOT_APPROVED" states
      const notApprovedStates = transitions.filter((transition: TransitionLogSignature) =>
        transition.to.includes('NOT_APPROVED')
      );

      // If there are no "NOT_APPROVED" states, return null or an empty result
      if (notApprovedStates.length === 0) {
        return null;
      }

      // Get the last occurrence based on the timestamp
      const lastNotApproved = notApprovedStates.reduce((latest, current) =>
        new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
      );

      // Extract the relevant details
      return {
        rejectedBy: lastNotApproved.reason?.by?.name || 'Unknown',
        note: lastNotApproved.reason?.note || 'No note provided',
      };
    };
    // spreeeaaad operator
    return { ...findNotApprovedDetails(this.args.model.application.transitionLog) };
  }

  get changesNeeded() {
    const lastTransition = this.args.model.application.transitionLog.slice(-1)[0];
    return lastTransition?.from.includes('NOT_APPROVED') || false;
  }

  get calculateTotalByGPA() {
    return this.programTemplate.calculateTotalByGPA;
  }

  get showGPAField() {
    return (
      this.calculateTotalByGPA &&
      (this.typeClassification === 'TAS.ProgramType.3' ||
        this.typeClassification === 'TAS.ProgramType.4.3')
    );
  }

  get currentGradePointAverage() {
    const value = getSingleValueForTasField(
      'GRADE_POINT_AVERAGE',
      this.args.model.application.fields
    );
    return value === null || value === undefined ? '' : String(value);
  }

  get isPendingGradePointAverageMissing() {
    return this.showGPAField && this.pendingGradePointAverage.trim() === '';
  }

  get requestedTotal() {
    if (this.showGPAField) {
      return this.setRequestedTotalAmount;
    } else {
      return this.args.model.application.requestedTotal;
    }
  }

  get isPaid() {
    return this.args.model.application.isPaid;
  }

  get courseCashUsed() {
    return this.args.model.application.amounts?.course_cash?.used > 0;
  }

  get isActiveParticipant() {
    return (
      this.args.model.application.tasProgramInstance.employee?.tasParticipant?.isActive || false
    );
  }

  get disableUploadAttachments() {
    return ['TAS.ApplicationState.DEFINE_COURSES', 'TAS.ApplicationState.ABANDONED'].includes(
      this.args.model.application.state
    );
  }

  get shouldShowPaymentDetailsCard() {
    return (
      this.args.model.application.shouldDisplayPaymentDetails &&
      this.args.model.application.paymentsHistory.length > 0
    );
  }

  updateGPA = (value: string) => {
    this.pendingGradePointAverage = value;
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(Number(value || 0));
  };

  saveGPA = async () => {
    const gpa = Number(this.pendingGradePointAverage);
    setSingleValueForTasField('GRADE_POINT_AVERAGE', gpa, this.args.model.application.fields);
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(gpa);
    setSingleValueForTasField(
      'FINAL_APPROVED_TOTAL',
      this.setRequestedTotalAmount,
      this.args.model.application.fields
    );
    await this.args.model.application.save();
  };

  saveGradeResultTask = dropTask(async () => {
    if (this.showGPAField) {
      await this.saveGPA();
    }
    this.closeGradeModal();
  });

  closeApplicationSubmittedModal = () => {
    this.args.controller.applicationSubmitted = false;
  };

  didUpdateAcceptedConditions = (conditions: ConditionModel[] = []) => {
    this.acceptedConditions = conditions;
  };

  scrollToAttachments = () => {
    document
      .getElementById('attachments-section')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  scrollToCourses = () => {
    document
      .getElementById('courses-section')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.highlightAddResult = true;
    setTimeout(() => (this.highlightAddResult = false), 2000);
  };

  openGradeModal = (course: TASCourseModel) => {
    this._pendingGrade = null;
    this.pendingGradePointAverage = this.currentGradePointAverage;
    this.isGradeModalOpen = true;
    this.courseToEdit = course;
  };

  closeGradeModal = () => {
    this.isGradeModalOpen = false;
    this._pendingGrade = null;
    // Delay clearing courseToEdit until after the reimagined Modal's
    // 300ms close animation completes to avoid rendering undefined
    setTimeout(() => {
      this.courseToEdit = null;
    }, 350);
  };

  onSelectionChange = async (key: string | null) => {
    this._pendingGrade = key;
    setSingleValueForTasField('COURSE_GRADE', key, this.courseToEdit!.fields);
    await this.courseToEdit!.save();
    this._pendingGrade = null;
  };

  openCourseDrawer = (course: TASCourseModel) => {
    this.courseShowing = course;
    this.isCourseDrawerOpen = true;
  };

  closeCourseDrawer = () => {
    this.courseShowing = null;
    this.isCourseDrawerOpen = false;
  };

  toggleDeleteApplicationModal = () => {
    this.deleteApplicationOpen = !this.deleteApplicationOpen;
  };

  openPartnerMessagingDrawer = () => {
    this.isPartnerMessagingDrawerOpen = true;
  };

  closePartnerMessagingDrawer = () => {
    this.isPartnerMessagingDrawerOpen = false;
  };

  get showTrashIcon() {
    if (this.isPaid || this.courseCashUsed) {
      return false;
    }
    const statesNotAllowed = [
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];
    if (statesNotAllowed.includes(this.args.model.application.state)) {
      return false;
    }
    if (
      this.typeClassification === 'TAS.ProgramType.4.2' ||
      this.typeClassification === 'TAS.ProgramType.4.3'
    ) {
      return (
        this.args.model.application.tasProgramInstance.state === 'TAS.ProgramInstanceState.PRIMED'
      );
    }

    return true;
  }

  get showChatIcon() {
    const companySetting =
      this.args.model.application?.tasProgramInstance?.tasProgramTemplate?.company
        ?.companySetting ?? {};
    if (companySetting?.tuitionAssistance?.isPartnerMessagingEnabled) {
      return true;
    }
    return false;
  }

  get isCardUsageReviewEnabled() {
    const companySetting =
      this.args.model.application?.tasProgramInstance?.tasProgramTemplate?.company?.companySetting;
    // Default to true (enabled) when not explicitly configured
    return companySetting?.tuitionAssistance?.isCardUsageReviewEnabled !== false;
  }
  // This is just the total number of messages for now.
  get numberOfUnreadMessages() {
    const partnerMessages = this.args.model.application.comments.filter((comment) =>
      comment.tags?.includes('Partner Messaging')
    );
    const messageCount = partnerMessages.length;
    return messageCount > 0 ? messageCount : '';
  }

  // Course Cash Receipt Upload Handlers
  handleUploadReceipt = (transaction: CourseCashTransaction) => {
    this.selectedReceiptTransaction = transaction;
    this.isReceiptModalOpen = true;
  };

  handleCloseReceiptModal = () => {
    this.isReceiptModalOpen = false;
    this.selectedReceiptTransaction = null;
  };

  handleReceiptUpload = async (
    files: DigitalAssetDetailSignature[],
    transaction: CourseCashTransaction
  ) => {
    if (!transaction.assistanceId) {
      throw new Error('No assistance ID found for transaction');
    }

    // Find the assistance record
    const assistance = this.args.model.application.tasAssistances?.find(
      (a) => a.id === transaction.assistanceId
    );

    if (!assistance) {
      throw new Error('Could not find assistance record');
    }

    // Upload each file and create asset + attachment with CARD_USAGE_PROOF classification
    for (const fileDetails of files) {
      // Create TasAsset from S3 upload details
      const asset = this.digitalAssets.createAssetFromS3Details(
        'tasAsset',
        fileDetails
      ) as TasAssetModel;
      asset.classification = 'CARD_USAGE_PROOF';

      const application = this.args.model.application;

      // Link asset to assistance - update both sides of many-to-many relationship
      asset.tasAssistances = asset.tasAssistances
        ? [...asset.tasAssistances, assistance]
        : [assistance];

      // Link asset to application - update both sides of many-to-many relationship
      asset.tasApplications = asset.tasApplications
        ? [...asset.tasApplications, application]
        : [application];
      application.tasAssets.push(asset);

      // Save the asset to create the join record; avoid saving the assistance
      // because tas-assistances has read-only attributes on update.
      await asset.save();
    }

    // Abandon any existing pending approvables — uploading a new receipt supersedes them.
    // The new asset will get its own approvable via TasAssetClassificationJob.
    const pendingReceipts = transaction.receipts.filter(
      (r) => r.status === 'pending_verification' && r.approvableId
    );
    for (const receipt of pendingReceipts) {
      if (!receipt.approvableId) continue;
      try {
        await this.store.request(abandonTasApprovableRequest(receipt.approvableId));
      } catch (error) {
        console.error('[CourseCash] Failed to abandon pending approvable on re-upload:', error);
      }
    }

    await this.reloadCourseCashAssistances();
  };

  handleRemoveReceipt = async (receipt: CourseCashReceipt, transaction: CourseCashTransaction) => {
    if (!receipt.attachmentId) {
      throw new Error('No attachment ID found for receipt');
    }

    let attachment = this.store.peekRecord(
      'attachment',
      receipt.attachmentId
    ) as AttachmentModel | null;

    if (!attachment) {
      attachment = await this.store.findRecord('attachment', receipt.attachmentId);
    }

    if (!attachment) {
      throw new Error('Could not find attachment for receipt');
    }

    await attachment.destroyRecord();

    // Abandon the associated TasApprovable when a receipt is removed.
    // Intentionally done AFTER destroyRecord: if abandon fails the orphaned
    // approvable is harmless (determineReceiptStatus handles it), but the
    // reverse — abandoning then failing to delete — leaves a verified-looking
    // file attached with no active review, which is worse.
    if (receipt.approvableId) {
      try {
        await this.store.request(abandonTasApprovableRequest(receipt.approvableId));
      } catch (error) {
        console.error('[CourseCash] Failed to abandon approvable:', error);
        // Non-blocking: the reload below will refresh state from the server
      }
    }

    await this.reloadCourseCashAssistances();

    const updatedTransaction = this.courseCashTransactions.find((t) => t.id === transaction.id);
    if (updatedTransaction) {
      this.selectedReceiptTransaction = updatedTransaction;
    }
  };

  reloadCourseCashAssistances = async () => {
    // Note: Do NOT include 'tas-assistances.approvables' here. The backend returns
    // approvables with the base STI type "approvables" which has no Ember Data model.
    // We query /tas-approvables directly in loadCourseCashTransactions() instead,
    // which returns the correct "tas-approvables" type.
    const assistanceIncludes = [
      'tas-assistances',
      'tas-assistances.attachments',
      'tas-assistances.tas-assets',
      'tas-assistances.tas-assets.attachments',
    ];
    await this.args.model.application.reload({
      include: assistanceIncludes.join(','),
    });

    await this.loadCourseCashTransactions();
  };

  deleteApplication = dropTask(async () => {
    try {
      if (this.isPaid || this.courseCashUsed) {
        this.toggleDeleteApplicationModal();
        return;
      } else {
        const appAdapter = this.store.adapterFor(
          'tas-application'
        ) as unknown as TasApplicationAdapter;
        if (['TAS.ProgramType.2', 'TAS.ProgramType.3'].includes(this.typeClassification)) {
          const actor = buildTasEmployeeActor(
            this.sessionContext.user,
            this.sessionContext.currentRole
          );
          await Promise.all([
            appAdapter.abandonCourse(this.args.model.application),
            this.store.request(
              abandonProgramRequest(
                this.args.model.application.tasProgramInstance.id as string,
                actor
              )
            ),
          ]);
        } else {
          await appAdapter.abandonCourse(this.args.model.application);
        }
      }
    } catch (error) {
      console.error('Failed to delete application:', error);
    }
    // TAS.TODO: temporary routing until tas dashboard is complete
    this.router.transitionTo('authenticated.tas.dashboard');
  });

  <template>
    <VStack>
      <Header>
        <div>
          {{! TAS.TODO: temporary routing until tas dashboard is complete }}
          <LinkTo class="flex justify-left items-center gap-4" @route="authenticated.tas.dashboard">
            <PhCaretLeft @size="20" class="inline" />
            {{t "tas.default"}}
          </LinkTo>
        </div>
      </Header>
      <HStack>
        <VStack class="w-full!">
          <div class="flex items-center justify-between w-full mb-4">
            <NavTabs
              class="flex-1"
              @tabs={{array
                (hash
                  label=(t "tas.applications.default")
                  route="authenticated.tas.applications.show"
                  model=@model.application
                )
                (hash
                  label=(t "tas.history.default")
                  route="authenticated.tas.applications.history"
                  model=@model.application
                )
              }}
            />
            <div class="flex items-center gap-2">
              {{#if this.showChatIcon}}
                <Button
                  @appearance="minimal"
                  {{on "click" this.openPartnerMessagingDrawer}}
                  class="mb-2"
                  data-test-partner-messaging-button
                >
                  <PhChatCenteredText @size="32" />
                  {{this.numberOfUnreadMessages}}
                </Button>
              {{/if}}
              {{#if this.showTrashIcon}}
                <Button
                  @appearance="minimal"
                  @intent="danger"
                  {{on "click" this.toggleDeleteApplicationModal}}
                  class="mb-2 text-danger"
                >
                  <PhTrash @size="32" />
                </Button>
              {{/if}}
            </div>
          </div>
        </VStack>
      </HStack>
      <HStack>
        <VStack class="w-full lg:w-2/3 lg:flex-none md:w-1/2 md:flex-none">
          {{! Course Cash Receipt Upload Banner — only when card usage review is enabled }}
          {{#if this.isCardUsageReviewEnabled}}
            <ReceiptBanner
              @transactions={{this.courseCashTransactions}}
              @onUploadReceipt={{this.handleUploadReceipt}}
            />
          {{/if}}

          {{#if this.hasPcsCards}}
            <StripeCardDisplay
              @application={{@model.application}}
              @transactions={{this.courseCashTransactions}}
              @onUploadReceipt={{if this.isCardUsageReviewEnabled this.handleUploadReceipt}}
              @showReceiptStatus={{this.isCardUsageReviewEnabled}}
            />
          {{/if}}

          <Section>
            <:header>
              {{String @model.application.displayNameOrSemesterCode}}
              <CourseCashTag
                @isDirectPayProgram={{this.programTemplate.isDirectPayProgram}}
                @size="sm"
                class="font-semibold"
              />
            </:header>
            <:body>
              {{#if this.inEligible}}
                <AlertBanner
                  @intent="warning"
                  @title={{t
                    "start_date_eligibility.errorHeader"
                    startEndSubmission=this.lockStatus
                  }}
                  data-test-eligibility-error
                  class="mb-4"
                >
                  <p class="text-sm">
                    {{t "start_date_eligibility.errorText"}}
                  </p>
                </AlertBanner>
              {{/if}}
              {{#if this.changesNeeded}}
                <AlertBanner
                  @intent="warning"
                  @title={{t
                    "tas.application.requested_changes"
                    name=this.requestedChanges.rejectedBy
                  }}
                  data-test-requested-changes
                  class="mb-4"
                >
                  <p class="text-sm">
                    {{this.requestedChanges.note}}
                  </p>
                </AlertBanner>
              {{/if}}

              {{#unless this.isActiveParticipant}}
                <AlertBanner
                  @intent="warning"
                  @title={{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
                  class="mb-4"
                >
                  <p class="text-sm">
                    {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
                  </p>
                </AlertBanner>
              {{/unless}}
              <Alert @application={{@model.application}} />
              <h2 class="font-medium text-tio-gray-700 my-4">
                {{@model.application.tasProgramInstance.programName}}
              </h2>
              {{! APPLICANT TABLE }}
              <Table @isLoading={{false}}>
                <:thead as |options|>
                  <options.tr>
                    <options.th>
                      {{t "tas.applications.applicant"}}
                    </options.th>
                    <options.th>
                      {{t "tas.applications.amount"}}
                    </options.th>
                    <options.th>{{t "tas.applications.submission_date"}}</options.th>
                    <options.th>{{t "tas.applications.status"}}</options.th>
                  </options.tr>
                </:thead>
                <:tbody as |options|>
                  <options.tr>
                    <options.td>{{@model.application.applicant}}</options.td>
                    <options.td>
                      {{safeFormatNumber
                        (divide this.requestedTotal 100)
                        style="currency"
                        currency="USD"
                      }}
                    </options.td>
                    <options.td>
                      {{safeFormatDate @model.application.applicationSubmittedDate}}
                    </options.td>
                    <options.td>
                      {{#if @model.application.inRepayment}}
                        <Chip @intent="warning">
                          {{t "tuition_assistance.application_details.in_repayment"}}
                        </Chip>
                      {{else}}
                        <Chip @intent={{@model.application.badgeIntent}}>
                          {{@model.application.badgeLabel}}
                        </Chip>
                      {{/if}}
                    </options.td>
                  </options.tr>
                </:tbody>
              </Table>
            </:body>
          </Section>
          <Section>
            <:header>{{t "tas.applications.details"}}</:header>
            <:body>
              {{! APPLICATION DETAILS }}
              <DescriptionList class="w-2/3 md:w-full" as |List|>
                <List.Group>
                  <List.Term>{{t "tas.applications.application_name"}}</List.Term>
                  <List.Details>{{@model.application.applicationName}}</List.Details>
                </List.Group>
                {{#if this.showDependentInfo}}
                  <List.Group>
                    <List.Term>{{t "tas.applications.dependent"}}</List.Term>
                    <List.Details
                    >{{@model.application.tasProgramInstance.dependentName}}</List.Details>
                  </List.Group>
                {{/if}}
                <List.Group>
                  <List.Term>{{t "tas.applications.start_date"}}</List.Term>
                  <List.Details>
                    {{formatDateString @model.application.coursesBeginDate}}
                  </List.Details>
                </List.Group>
                <ProgramReadonlyField
                  @name="SEMESTER_CODE"
                  @programTemplate={{this.programTemplate}}
                  @fields={{@model.application.fields}}
                  as |field|
                >
                  <List.Group>
                    <List.Term>{{field.programLabel}}</List.Term>
                    <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                  </List.Group>
                </ProgramReadonlyField>
                {{#if this.isTypeTwoOrThreeApplication}}
                  <ProgramReadonlyField
                    @name="SCHOOL_INSTITUTION_NAME"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="PROGRAM_MAJOR"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="PROGRAM_SPECIALIZATION"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="ESTIMATED_PROGRAM_BEGIN"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{formatDateString field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="ESTIMATED_PROGRAM_COMPLETION"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{formatDateString field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="ENROLLMENT_OBJECTIVE"
                    {{! @glint-expect-error: not sure }}
                    @programTemplate={{@model.application.programInstance.tasProgramTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  {{#each this.programCustomFields as |field|}}
                    <ProgramCustomReadonlyField @customField={{field}} as |field|>
                      <List.Group>
                        <List.Term>{{field.programLabel}}</List.Term>
                        <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                      </List.Group>
                    </ProgramCustomReadonlyField>
                  {{/each}}
                {{/if}}
              </DescriptionList>
            </:body>
          </Section>
          {{! COURSE DETAILS - EXPENSES }}
          <Section id="courses-section">
            <:header>{{this.expenseType}}</:header>
            <:body>
              <ActionableList @striped={{true}} as |l|>
                {{#each @model.application.activeCourses as |course|}}
                  <l.Row class="w-full">
                    <l.Term
                      class="w-96 cursor-pointer text-ocean-600 underline"
                      {{on "click" (fn this.openCourseDrawer course)}}
                    >
                      {{course.courseName}}
                    </l.Term>
                    <l.Item
                      class="w-96 cursor-pointer"
                      {{on "click" (fn this.openCourseDrawer course)}}
                    >
                      {{#if @model.application.isCreditBased}}
                        {{course.courseCredit}}
                        {{t "common.credits"}}
                      {{else}}
                        {{safeFormatNumber
                          (divide course.courseTotal 100)
                          style="currency"
                          currency="USD"
                        }}
                      {{/if}}
                    </l.Item>
                    {{#if this.applicationAwaitingApproval}}
                      <l.Item
                        class="ml-0 mr-2 flex items-center font-semibold text-ocean-600 cursor-pointer md:ml-auto"
                      >
                        <Chip @intent={{this.courseBadge.intent}}>
                          {{this.courseBadge.label}}
                        </Chip>
                      </l.Item>
                    {{else if this.showAddResult}}
                      <l.Item
                        class="ml-0 mr-2 flex items-center font-semibold cursor-pointer md:ml-auto transition-all duration-300 rounded px-2 py-0.5
                          {{if
                            this.highlightAddResult
                            'text-violet-600 ring-2 ring-violet-400 ring-offset-1'
                            'text-ocean-600'
                          }}"
                        {{on "click" (fn this.openGradeModal course)}}
                      >
                        {{#if course.courseGrade}}
                          {{course.courseGrade}}
                          <PhPencil @size="24" class="ml-2 text-tio-gray-700" />
                        {{else}}
                          {{t "tas.benefits_summary.add_result_simple"}}
                        {{/if}}
                      </l.Item>
                    {{else if this.showCompletedGrade}}
                      <l.Item
                        class="ml-0 mr-2 flex items-center font-semibold text-ocean-600 md:ml-auto"
                      >
                        {{course.courseGrade}}
                      </l.Item>
                    {{/if}}
                  </l.Row>
                {{/each}}
              </ActionableList>
            </:body>
          </Section>
          {{#if
            (and this.programTemplate.scholarshipsAreVisible @model.application.scholarships.length)
          }}
            <Section>
              <:header>
                {{t "tas.new.scholarships.default"}}
              </:header>
              <:body>
                {{! SCHOLARSHIPS }}
                <ActionableList @striped={{true}} as |l|>
                  {{#each @model.application.scholarships as |scholarship|}}
                    <l.Row class="w-full">
                      <l.Term class="w-96">{{scholarship.scholarshipName}}</l.Term>
                      <l.Item class="w-96">
                        {{safeFormatNumber
                          (divide scholarship.scholarshipAmount 100)
                          style="currency"
                          currency="USD"
                        }}
                      </l.Item>
                    </l.Row>
                  {{/each}}
                </ActionableList>
              </:body>
            </Section>
          {{/if}}

          {{! ATTACHMENTS }}
          {{#if this.shouldShowAttachments}}
            <Section id="attachments-section">
              <:header>
                <div>
                  {{t "common.attachments"}}
                  {{#if this.isAttachmentRequired}}
                    <span class="text-error-400 text-xl">*</span>
                  {{/if}}
                </div>
              </:header>
              <:body>
                {{#if this.useCategorizedUpload}}
                  <CategorizedUpload
                    @programTemplate={{this.programTemplate}}
                    @classification={{this.assetClassificationString}}
                    @attachable={{@model.application}}
                    @attachableType="tasApplication"
                    @existingAssets={{@model.application.tasAssets}}
                    @disabled={{this.disableUploadAttachments}}
                  />
                {{else}}
                  <UploadAttachments
                    @programTemplate={{this.programTemplate}}
                    @classification={{this.assetClassificationString}}
                    @attachable={{@model.application}}
                    @attachableType="tasApplication"
                    @useDropzone={{true}}
                    @hideInstructions={{not this.showInstructions}}
                    @disabled={{this.disableUploadAttachments}}
                    @attachmentListItems={{this.allAttachments}}
                    @hideDiscarded={{true}}
                  />
                {{/if}}
                <div class="flex items-center w-full">
                </div>
              </:body>
            </Section>
          {{/if}}
        </VStack>
        {{! Evidence Submission and Payment Details }}
        <VStack class="lg:w-1/3! md:w-1/2!">
          {{! Submit For Evidence Review - ASIDE }}
          {{#if this.showSubmitForEvidenceReview}}
            <Section
              class="w-full"
              @headerClass="flex text-sm items-center gap-2 font-medium tracking-wide"
            >
              <:header>
                <PhClipboardText @size="32" />
                <p class="text-balance">{{t "tas.application.submit_evidence_for_review"}}</p>
              </:header>
              <:body>
                <section>
                  {{#if @model.application.isEvidenceRequired}}
                    <button
                      type="button"
                      class="flex items-center gap-3 cursor-pointer group bg-transparent border-0 p-0 w-full text-left"
                      aria-label={{t "tas.benefits_summary.attachments"}}
                      {{on "click" this.scrollToAttachments}}
                    >
                      {{#if @model.application.isMissingRequiredEvidence}}
                        <PhCircle
                          @size="24"
                          class="text-tio-gray-200 group-hover:text-violet-500 shrink-0 transition-colors"
                        />
                      {{else}}
                        <PhCheckCircle @size="24" @weight="fill" class="text-violet-500 shrink-0" />
                      {{/if}}
                      <span
                        class="group-hover:text-violet-600 group-hover:underline transition-colors"
                      >{{t "tas.benefits_summary.attachments"}}</span>
                    </button>
                  {{/if}}

                  {{#if @model.application.isCourseGradeRequired}}
                    <button
                      type="button"
                      class="flex items-center gap-3 cursor-pointer group bg-transparent border-0 p-0 w-full text-left"
                      aria-label={{t "tas.application.course_grades"}}
                      {{on "click" this.scrollToCourses}}
                    >
                      {{#if @model.application.isMissingCourseGrades}}
                        <PhCircle
                          @size="24"
                          class="text-tio-gray-200 group-hover:text-violet-500 shrink-0 transition-colors"
                        />
                      {{else}}
                        <PhCheckCircle @size="24" @weight="fill" class="text-violet-500 shrink-0" />
                      {{/if}}
                      <span
                        class="group-hover:text-violet-600 group-hover:underline transition-colors"
                      >{{t "tas.application.course_grades"}}</span>
                    </button>
                  {{/if}}

                  {{#if
                    (or
                      @model.application.isEvidenceRequired @model.application.isCourseGradeRequired
                    )
                  }}
                    <Divider class="mt-4" />
                  {{/if}}

                  {{#if this.isLpnSchool}}
                    <div class="flex items-start gap-3 mt-4">
                      <Checkbox
                        @checked={{@model.application.lpnDiscountActivated}}
                        @onChange={{this.toggleLpnDiscountActivated}}
                      />
                      <div class="flex items-center gap-1">
                        <span class="text-sm">{{t "lpn.discount_checkbox.label"}}</span>
                        <button
                          type="button"
                          class="inline-flex items-center justify-center text-gray-400 cursor-help"
                          title={{t "lpn.discount_checkbox.tooltip"}}
                          aria-label={{t "lpn.discount_checkbox.tooltip"}}
                        >
                          <PhInfo @size="16" />
                        </button>
                      </div>
                    </div>
                  {{/if}}

                  {{#if this.conditionsForSubmit.length}}
                    <AcceptConditions
                      @conditions={{this.conditionsForSubmit}}
                      @onChange={{this.didUpdateAcceptedConditions}}
                    />
                  {{/if}}
                </section>

                <div class="py-4 text-center">
                  <Button
                    @intent="primary"
                    @isDisabled={{not this.canSubmitApplication}}
                    @isRunning={{this.submitForEvidenceApproval.isRunning}}
                    {{on "click" this.submitForEvidenceApproval.perform}}
                    class="my-4"
                  >
                    {{t "tas.benefits_summary.submit_evidence"}}
                  </Button>
                  <p class="text-sm font-light">
                    {{t "tas.benefits_summary.helper_text"}}
                  </p>
                  {{#if this.submitError}}
                    <ErrorMessages
                      @showErrors={{this.hasSubmitted}}
                      @error={{this.submitError}}
                      class="text-sm"
                    />
                  {{/if}}
                </div>
              </:body>
            </Section>
          {{/if}}

          <PaymentDetailsCard
            @showDetails={{this.shouldShowPaymentDetailsCard}}
            @requestedTotal={{this.requestedTotal}}
            @payments={{@model.application.paymentsHistory}}
            @isCreditBased={{@model.application.isCreditBased}}
            @totalCredits={{@model.application.totalCredits}}
          />

          <ClaimsFinancialsCalculationsReadonlyCard
            class="grow md:max-w-xl"
            @claimsFinancialsFields={{@model.application.claimsFinancialsCalculatorInput}}
            @showDetails={{@model.application.tasProgramInstance.tasProgramTemplate.calculateTotalByClaimsFinancials}}
          />

          <ApplicationProcess
            @application={{@model.application}}
            @programTemplate={{this.programTemplate}}
          />
          <TasFaqs @template={{this.programTemplate}} />
        </VStack>
      </HStack>
    </VStack>

    <Modal
      @isOpen={{@controller.applicationSubmitted}}
      @onClose={{this.closeApplicationSubmittedModal}}
      class="min-h-64 max-w-screen-sm!"
    >
      <:header>
        <span class="text-midnight font-semibold">{{t
            "tuition_assistance.submitted.application_submitted"
          }}</span>
      </:header>
      <:body>
        <CourseSubmitted @application={{@model.application}} @courseApprovalRequired={{true}} />
        <div class="mb-4 flex justify-center">
          <Button
            {{on "click" this.closeApplicationSubmittedModal}}
            @appearance="outlined"
            class="w-48"
          >
            {{t "common.close"}}
          </Button>
        </div>
      </:body>
    </Modal>
    <Modal @isOpen={{this.isGradeModalOpen}} @onClose={{this.closeGradeModal}}>
      <:header>
        <h2 class="text-midnight font-semibold">{{t "tas.benefits_summary.add_result"}}</h2>
      </:header>
      <:body>
        {{#if this.courseToEdit}}
          <div class="flex justify-between mb-6">
            <h2 class="text-lg font-semibold text-gray-700">
              {{String (getSingleValueForTasField "COURSE_NAME" this.courseToEdit.fields)}}
            </h2>
            <div class="w-72">
              <Select
                @items={{this.grades}}
                @selected={{this.selectedGrade}}
                @onChange={{this.onSelectionChange}}
              />
            </div>
          </div>
          {{#if this.showGPAField}}
            <div class="flex flex-wrap justify-between gap-y-4 items-center">
              <label
                for="grade-point-average"
                class="font-semibold block mb-1 text-balance text-gray-600 flex-1 min-w-56"
              >
                {{t "tas.application.grade_point_average"}}
                <span class="text-error-400">*</span>
              </label>
              <Input
                @size="lg"
                type="number"
                min="0"
                max="4.0"
                step="0.01"
                data-test-grade-point-average
                id="grade-point-average"
                @value={{this.pendingGradePointAverage}}
                class="bg-transparent! w-40! min-w-40!"
                @onChange={{this.updateGPA}}
              />
            </div>
          {{/if}}
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.saveGradeResultTask.isRunning}}
          @onCancel={{this.closeGradeModal}}
          @onSubmit={{this.saveGradeResultTask.perform}}
          @submitDisabled={{and this.showGPAField this.isPendingGradePointAverageMissing}}
        />
      </:footer>
    </Modal>
    <Modal
      @isOpen={{this.deleteApplicationOpen}}
      @onClose={{this.toggleDeleteApplicationModal}}
      class="max-w-screen-sm!"
    >
      <:header>
        {{#unless this.isPaid}}
          {{t "tas.application.delete_modal.title"}}
        {{/unless}}
      </:header>
      <:body>
        {{#if this.isPaid}}
          <p class="font-bold text-red-800 mb-4">
            {{t "tas.application.delete_modal.attention"}}
          </p>

          <p class="text-red-700">
            {{t "tas.application.delete_modal.prepaid_warning"}}
          </p>
        {{else}}
          <p class="mb-4">{{t "tas.application.delete_modal.content"}}</p>
        {{/if}}
      </:body>
      <:footer>
        {{#if this.isPaid}}
          <Footer
            @onSubmit={{this.toggleDeleteApplicationModal}}
            @submitText={{t "common.close"}}
          />
        {{else}}
          <Footer
            @isRunning={{this.deleteApplication.isRunning}}
            @onCancel={{this.toggleDeleteApplicationModal}}
            @onSubmit={{this.deleteApplication.perform}}
            @submitText={{t "common.delete"}}
          />
        {{/if}}
      </:footer>
    </Modal>
    {{#if this.isCourseDrawerOpen}}
      <ShowCourseDrawer
        @isOpen={{this.isCourseDrawerOpen}}
        @onClose={{this.closeCourseDrawer}}
        @course={{this.courseShowing}}
        @programTemplate={{this.programTemplate}}
        {{! @glint-ignore: To reuse this for all courses course needs to be set to null on close }}
        @header={{this.courseShowing.courseName}}
      />
    {{/if}}
    {{#if this.isPartnerMessagingDrawerOpen}}
      <PartnerMessagingDrawer
        @isOpen={{this.isPartnerMessagingDrawerOpen}}
        @onClose={{this.closePartnerMessagingDrawer}}
        @application={{@model.application}}
      />
    {{/if}}
    {{! Course Cash Receipt Upload Modal }}
    {{#if this.isCardUsageReviewEnabled}}
      <ReceiptUploadModal
        @isOpen={{this.isReceiptModalOpen}}
        @transaction={{this.selectedReceiptTransaction}}
        @onClose={{this.handleCloseReceiptModal}}
        @onUpload={{this.handleReceiptUpload}}
        @onRemoveReceipt={{this.handleRemoveReceipt}}
      />
    {{/if}}
  </template>
}
